import React, { forwardRef, useEffect, useId, useImperativeHandle, useMemo, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import ChevronDown from 'design-system/src/assets/icons/chevronDown.svg?react';
import { Popover, PopoverButton, PopoverPanel } from 'design-system/src/components/Popover/Popover';
import { DateInput } from 'design-system/src/components/Input/Input';
import Button from 'design-system/src/components/Button/Button';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { dropdownTriggerStyles } from 'design-system/src/sharedStyles';

const FilterButton = styled(PopoverButton)<{ $isActive: boolean }>`
  ${dropdownTriggerStyles}
  // Matches Typography styledAs="bodySmallDMSans" from MultiselectAutocomplete triggers.
  font-family: 'DM Sans', system-ui, Helvetica, Arial, sans-serif;
  font-size: 0.875rem;
  line-height: 1.2857;
  font-weight: ${({ $isActive }) => ($isActive ? 500 : 400)};

  &[data-headlessui-state~='open'] {
    background-color: var(--grayscale-50);
    border-color: var(--grayscale-50);
  }

  ${({ $isActive }) => ($isActive ? 'border-color: var(--purple-200);' : '')}
  ${({ $isActive }) => ($isActive ? 'background-color: var(--purple-200) !important;' : '')}
`;

const ActiveBadge = styled(Typography).attrs({
  styledAs: 'bodyExtraExtraSmallDMSans',
  renderedAs: 'span',
  weight: 600,
})`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 16px;
  background: var(--grayscale-900);
  color: var(--purple-200);
`;

const DatePopoverPanel = styled(PopoverPanel)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  border-radius: 10px;
`;

const InputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled(Typography).attrs({
  styledAs: 'bodyExtraSmallSpaceGrotesk',
  renderedAs: 'label',
  weight: 500,
})``;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const FUTURE_ERROR_COPY = 'The date must not be in the future';

export type DateFilterHandle = {
  clear: () => void;
};

export type DateFilterProps = {
  startDate?: string;
  endDate?: string;
  onApply: (dates: { startDate?: string; endDate?: string }) => void;
  onClear: () => void;
  label?: string;
} & React.HTMLAttributes<HTMLDivElement>;

const parseDate = (value?: string | null) => {
  if (!value) {
    return null;
  }
  const parsed = moment(value, moment.ISO_8601, true);
  return parsed.isValid() ? parsed : null;
};

const formatForApi = (value: moment.Moment | null) => {
  if (!value || !value.isValid()) {
    return undefined;
  }
  return value.format('YYYY-MM-DD');
};

export const DateFilter = forwardRef<DateFilterHandle, DateFilterProps>(
  ({ startDate, endDate, onApply, onClear, label = 'Date', ...rest }, ref) => {
    const [startInputValue, setStartInputValue] = useState<moment.Moment | null>(() => parseDate(startDate));
    const [endInputValue, setEndInputValue] = useState<moment.Moment | null>(() => parseDate(endDate));
    const [startErrorMessage, setStartErrorMessage] = useState<string | null>(null);
    const [endErrorMessage, setEndErrorMessage] = useState<string | null>(null);

    const startInputId = useId();
    const endInputId = useId();

    useImperativeHandle(ref, () => ({
      // Use in the "Clear filters" button in the table this component is used in.
      clear: () => {
        setStartInputValue(null);
        setEndInputValue(null);
        setStartErrorMessage(null);
        setEndErrorMessage(null);
        onClear();
      },
    }));

    useEffect(() => {
      // Keep the internal input state synchronized with the applied prop values.
      // Notes:
      // - State initializers above run only on the first render; they won't update when
      //   startDate/endDate props change later.
      // - Parents can change the applied dates outside of this menu (e.g., URL/search param sync,
      //   saved defaults, navigation), including while the menu is closed.
      // - Without this effect, inputs (and any validation errors) could become stale when
      //   props change externally.
      setStartInputValue(parseDate(startDate));
      setEndInputValue(parseDate(endDate));
      setStartErrorMessage(null);
      setEndErrorMessage(null);
    }, [startDate, endDate]);

    const appliedStart = useMemo(() => parseDate(startDate), [startDate]);
    const appliedEnd = useMemo(() => parseDate(endDate), [endDate]);

    const buttonAriaLabel = useMemo(() => {
      if (appliedStart && appliedEnd) {
        return `${appliedStart.format('MM/DD/YYYY')} - ${appliedEnd.format('MM/DD/YYYY')}`;
      }
      if (appliedStart) {
        return `From ${appliedStart.format('MM/DD/YYYY')}`;
      }
      if (appliedEnd) {
        return `Until ${appliedEnd.format('MM/DD/YYYY')}`;
      }
      return label;
    }, [appliedStart, appliedEnd, label]);

    const validateDates = (start: moment.Moment | null, end: moment.Moment | null) => {
      let startMessage: string | null = null;
      let endMessage: string | null = null;

      const now = moment();
      if (start && start.isAfter(now, 'day')) {
        startMessage = FUTURE_ERROR_COPY;
      }
      if (end && end.isAfter(now, 'day')) {
        endMessage = FUTURE_ERROR_COPY;
      }

      if (!startMessage && !endMessage && start && end) {
        if (start.isAfter(end)) {
          startMessage = 'The start date must be before the end date.';
          endMessage = 'The end date must be after the start date.';
        }
      }

      // Set error messages synchronously before returning validation result
      setStartErrorMessage(startMessage);
      setEndErrorMessage(endMessage);

      return !startMessage && !endMessage;
    };

    const handleStartChange = (value: moment.Moment | null) => {
      setStartInputValue(value);
      // Clear any existing error messages when user types
      setStartErrorMessage(null);
      setEndErrorMessage(null);
    };

    const handleEndChange = (value: moment.Moment | null) => {
      setEndInputValue(value);
      // Clear any existing error messages when user types
      setStartErrorMessage(null);
      setEndErrorMessage(null);
    };

    const handleEndFocus = () => {
      if (!endInputValue && startInputValue && startInputValue.isValid()) {
        const cloned = startInputValue.clone();
        setEndInputValue(cloned);
        // Clear any existing error messages when auto-filling end date
        setStartErrorMessage(null);
        setEndErrorMessage(null);
      }
    };

    const handleClear = (close: () => void) => {
      // Always clear the input fields for visual feedback
      setStartInputValue(null);
      setEndInputValue(null);
      setStartErrorMessage(null);
      setEndErrorMessage(null);

      // Check if there's currently no active filter before triggering onClear
      if (!appliedStart && !appliedEnd) {
        close();
        return;
      }

      onClear();
      close();
    };

    const handleApply = (close: () => void) => {
      const isValid = validateDates(startInputValue, endInputValue);
      if (!isValid) {
        return;
      }

      // Check if the values have actually changed before applying
      const currentStartFormatted = formatForApi(startInputValue);
      const currentEndFormatted = formatForApi(endInputValue);

      // Compare with the currently applied values
      if (currentStartFormatted === startDate && currentEndFormatted === endDate) {
        // Values haven't changed, just close the menu without triggering onApply
        close();
        return;
      }

      onApply({
        startDate: currentStartFormatted,
        endDate: currentEndFormatted,
      });
      // Only close the menu if validation passes
      close();
    };

    const hasActiveFilter = Boolean(appliedStart || appliedEnd);

    const buttonContent = hasActiveFilter ? (
      <>
        {label}
        <ActiveBadge aria-hidden="true">1</ActiveBadge>
      </>
    ) : (
      label
    );

    const isApplyDisabled = Boolean(startErrorMessage || endErrorMessage);

    return (
      <Popover {...rest}>
        <FilterButton $isActive={hasActiveFilter} aria-label={buttonAriaLabel}>
          {buttonContent}
          <ChevronDown aria-hidden />
        </FilterButton>

        <DatePopoverPanel anchor="bottom end" width="320px">
          {({ close }) => (
            <>
              <InputsContainer>
                <InputGroup>
                  <FieldLabel htmlFor={startInputId}>Start date</FieldLabel>
                  <DateInput
                    id={startInputId}
                    label="Start date"
                    hideLabel
                    value={startInputValue}
                    onChange={handleStartChange}
                    hasError={Boolean(startErrorMessage)}
                  />
                  {startErrorMessage && (
                    <ErrorMessage marginTop="4px" textStyledAs="bodyExtraSmallSpaceGrotesk">
                      {startErrorMessage}
                    </ErrorMessage>
                  )}
                </InputGroup>

                <InputGroup>
                  <FieldLabel htmlFor={endInputId}>End date</FieldLabel>
                  <DateInput
                    id={endInputId}
                    label="End date"
                    hideLabel
                    value={endInputValue}
                    onChange={handleEndChange}
                    onFocus={handleEndFocus}
                    hasError={Boolean(endErrorMessage)}
                  />
                  {endErrorMessage && (
                    <ErrorMessage marginTop="4px" textStyledAs="bodyExtraSmallSpaceGrotesk">
                      {endErrorMessage}
                    </ErrorMessage>
                  )}
                </InputGroup>
              </InputsContainer>

              <Footer>
                <Button variant="secondary" size="sm" type="button" onClick={() => handleClear(close)}>
                  Clear filter
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  type="button"
                  disabled={isApplyDisabled}
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    // Prevent the button from closing the menu before validating the dates
                    e.preventDefault();
                    e.stopPropagation();
                    handleApply(close);
                  }}
                >
                  Apply
                </Button>
              </Footer>
            </>
          )}
        </DatePopoverPanel>
      </Popover>
    );
  },
);

DateFilter.displayName = 'DateFilter';

export default DateFilter;
